// ========================================
// Form Validator
// Handles form validation with custom rules
// ========================================

const FormValidator = {
    /**
     * Validation rules
     */
    rules: {
        required: (value) => {
            return value && value.trim() !== '';
        },

        email: (value) => {
            if (!value) return true; // Optional field
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(value);
        },

        phone: (value) => {
            if (!value) return true; // Optional field
            const re = /^[+]?[\d\s-()]+$/;
            return re.test(value);
        },

        minLength: (value, length) => {
            if (!value) return true;
            return value.length >= length;
        },

        maxLength: (value, length) => {
            if (!value) return true;
            return value.length <= length;
        },

        pattern: (value, pattern) => {
            if (!value) return true;
            const re = new RegExp(pattern);
            return re.test(value);
        },

        number: (value) => {
            if (!value) return true;
            return !isNaN(value);
        },

        positiveNumber: (value) => {
            if (!value) return true;
            return !isNaN(value) && parseFloat(value) > 0;
        },

        time: (value) => {
            if (!value) return true;
            const re = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
            return re.test(value);
        },

        date: (value) => {
            if (!value) return true;
            const date = new Date(value);
            return date instanceof Date && !isNaN(date);
        }
    },

    /**
     * Error messages
     */
    messages: {
        required: 'Este campo es obligatorio',
        email: 'Introduce un email válido',
        phone: 'Introduce un teléfono válido',
        minLength: 'Debe tener al menos {length} caracteres',
        maxLength: 'No puede tener más de {length} caracteres',
        pattern: 'El formato no es válido',
        number: 'Debe ser un número',
        positiveNumber: 'Debe ser un número positivo',
        time: 'Introduce una hora válida (HH:MM)',
        date: 'Introduce una fecha válida'
    },

    /**
     * Validate a single field
     */
    validateField(field, validationRules) {
        const value = field.value;
        const errors = [];

        for (const [ruleName, ruleValue] of Object.entries(validationRules)) {
            const rule = this.rules[ruleName];
            if (!rule) {
                console.warn(`Validation rule '${ruleName}' not found`);
                continue;
            }

            let isValid;
            if (typeof ruleValue === 'boolean' && ruleValue) {
                isValid = rule(value);
            } else {
                isValid = rule(value, ruleValue);
            }

            if (!isValid) {
                let message = this.messages[ruleName] || 'Valor no válido';
                // Replace placeholders
                if (typeof ruleValue !== 'boolean') {
                    message = message.replace('{length}', ruleValue);
                }
                errors.push(message);
            }
        }

        return {
            valid: errors.length === 0,
            errors
        };
    },

    /**
     * Validate entire form
     */
    validateForm(formElement, validationConfig) {
        const results = {};
        let isFormValid = true;

        for (const [fieldName, rules] of Object.entries(validationConfig)) {
            const field = formElement.querySelector(`[name="${fieldName}"]`);
            if (!field) {
                console.warn(`Field '${fieldName}' not found in form`);
                continue;
            }

            const result = this.validateField(field, rules);
            results[fieldName] = result;

            if (!result.valid) {
                isFormValid = false;
                this.showFieldError(field, result.errors[0]);
            } else {
                this.clearFieldError(field);
            }
        }

        return {
            valid: isFormValid,
            results
        };
    },

    /**
     * Show error message for a field
     */
    showFieldError(field, message) {
        // Remove existing error
        this.clearFieldError(field);

        // Add error class to field
        field.classList.add('field-error');

        // Create error message element
        const errorElement = document.createElement('div');
        errorElement.className = 'field-error-message';
        errorElement.textContent = message;
        errorElement.setAttribute('data-error-for', field.name);

        // Insert error message after field
        field.parentNode.insertBefore(errorElement, field.nextSibling);
    },

    /**
     * Clear error message for a field
     */
    clearFieldError(field) {
        field.classList.remove('field-error');

        // Remove error message
        const errorElement = field.parentNode.querySelector(`[data-error-for="${Utils.sanitizeHTML(field.name)}"]`);
        if (errorElement) {
            errorElement.remove();
        }
    },

    /**
     * Clear all errors in a form
     */
    clearFormErrors(formElement) {
        const fields = formElement.querySelectorAll('.field-error');
        fields.forEach(field => {
            this.clearFieldError(field);
        });
    },

    /**
     * Add real-time validation to a field
     */
    addRealtimeValidation(field, rules) {
        field.addEventListener('blur', () => {
            const result = this.validateField(field, rules);
            if (!result.valid) {
                this.showFieldError(field, result.errors[0]);
            } else {
                this.clearFieldError(field);
            }
        });

        field.addEventListener('input', () => {
            // Clear error on input if field was previously invalid
            if (field.classList.contains('field-error')) {
                const result = this.validateField(field, rules);
                if (result.valid) {
                    this.clearFieldError(field);
                }
            }
        });
    }
};

// Make FormValidator globally available
window.FormValidator = FormValidator;
